import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
// import { knownMappings } from '../utils/linkMappings'; // No longer needed
import './PaymentPage.css';

const client = generateClient<Schema>();

// Declare the global SecureFields type
declare global {
  interface Window {
    SecureFields: any;
  }
}

interface PaymentLinkData {
  linkId: string;
  guestName?: string;
  guestEmail: string;
  locationName?: string;
  confirmationNumber?: string;    
  arrivalDate?: string;             
  departureDate?: string;
  amount: number;
  currency: string;
  status: string;
  expiresAt: string;
  metadata?: string;
  customerId?: string;
}

const PaymentPage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();
  const [paymentLink, setPaymentLink] = useState<PaymentLinkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [errorAttempts, setErrorAttempts] = useState(0);
  const [emailSent, setEmailSent] = useState(false);
  const secureFieldsRef = useRef<any>(null);

  // ALL useEffect hooks must be at the top, before any conditional returns
  useEffect(() => {
    if (linkId) {
      fetchPaymentLink();
      loadPCIProxyScript();
    }
  }, [linkId]);

  useEffect(() => {
    if (scriptLoaded && paymentLink && !secureFieldsRef.current) {
      initializeSecureFields();
    }
  }, [scriptLoaded, paymentLink]);

  // Check for replacement link when expired OR when link not found
  useEffect(() => {
    const checkForReplacement = async () => {
      if ((error === 'expired' || error === 'Payment link not found') && linkId) {
        try {
          // First try to find the original link with a replacement
          const result = await client.models.PaymentLink.list({
            filter: { linkId: { eq: linkId } }
          });
          
          if (result.data?.[0]?.replacementLinkId) {
            console.log('Found replacement link, redirecting...');
            navigate(`/pay/${result.data[0].replacementLinkId}`);
            return;
          }
          
          // If no original link found, search for any link that has this as replacement
          // This handles cases where the original link was never in the database
          const replacementSearch = await client.models.PaymentLink.list({
            filter: { replacementLinkId: { eq: linkId } }
          });
          
          if (replacementSearch.data && replacementSearch.data.length > 0) {
            console.log('This link is already a replacement, no further action needed');
            return;
          }
          
        } catch (err) {
          console.error('Error checking for replacement:', err);
        }
      }
    };
  
    if (error === 'expired' || error === 'Payment link not found') {
      checkForReplacement();
    }
  }, [error, linkId, navigate]);

  const loadPCIProxyScript = () => {
    // Check if script is already loaded
    if (window.SecureFields) {
      console.log('=== PCI PROXY SCRIPT ALREADY LOADED ===');
      setScriptLoaded(true);
      return;
    }

    console.log('=== LOADING PCI PROXY SCRIPT ===');
    console.log('Script URL:', 'https://pay.datatrans.com/upp/payment/js/secure-fields-2.0.0.js');
    console.log('Environment check - Production mode should be active');
    
    const script = document.createElement('script');
    script.src = 'https://pay.datatrans.com/upp/payment/js/secure-fields-2.0.0.js';
    script.onload = () => {
      console.log('=== PCI PROXY SCRIPT LOADED SUCCESSFULLY ===');
      console.log('SecureFields available:', !!window.SecureFields);
      setScriptLoaded(true);
    };
    script.onerror = () => {
      console.error('=== PCI PROXY SCRIPT FAILED TO LOAD ===');
      setError('Failed to load payment system');
    };
    document.head.appendChild(script);
  };

  // Function to find alternative/replacement links when original not found
  const findAlternativeLink = async (originalLinkId: string) => {
    try {
      console.log('Searching for alternative links for:', originalLinkId);
      
      // Search for any link that might reference this as a replacement
      const replacementSearch = await client.models.PaymentLink.list({
        filter: { replacementLinkId: { eq: originalLinkId } }
      });
      
      if (replacementSearch.data && replacementSearch.data.length > 0) {
        const activeReplacement = replacementSearch.data[0];
        console.log('Found replacement reference, redirecting to:', activeReplacement.linkId);
        navigate(`/pay/${activeReplacement.linkId}`);
        return true;
      }

      // TODO: In the future, implement more sophisticated lookup
      // based on customer email or confirmation number patterns
      
      return false; // No alternative found
    } catch (error) {
      console.error('Error searching for alternative links:', error);
      return false;
    }
  };

  const fetchPaymentLink = async () => {
    try {
      console.log('=== FETCHING PAYMENT LINK ===');
      console.log('Link ID:', linkId);
      console.log('Filter object:', { linkId: { eq: linkId } });
      
      // First try with the filter (using GSI)
      let result = await client.models.PaymentLink.list({
        filter: { linkId: { eq: linkId } }
      });
      
      // If GSI query returns empty, try a scan approach as fallback
      if (!result.data || result.data.length === 0) {
        console.log('GSI query returned empty, trying scan approach...');
        
        let scanResult = await client.models.PaymentLink.list({
          limit: 1000
        });
        
        let allRecords = scanResult.data || [];
        let nextToken = scanResult.nextToken;
        
        console.log('Initial scan returned', allRecords.length, 'records');
        
        // Paginate through all records if needed
        while (nextToken && allRecords.length < 3000) { // Cap at 3000 records for safety
          console.log('Fetching next page of records...');
          const nextPage = await client.models.PaymentLink.list({
            limit: 1000,
            nextToken: nextToken
          });
          
          if (nextPage.data) {
            allRecords = allRecords.concat(nextPage.data);
          }
          nextToken = nextPage.nextToken;
          console.log('Total records now:', allRecords.length);
        }
        
        console.log('Searching through', allRecords.length, 'total records for linkId:', linkId);
        
        // Filter client-side for the linkId
        const matchingLink = allRecords.find((link: any) => link.linkId === linkId);
        
        if (matchingLink) {
          result = { data: [matchingLink], nextToken: null };
          console.log('Found link via scan approach:', matchingLink);
        } else {
          console.log('No matching link found in all scan results');
          console.log('Sample linkIds from scan:', allRecords.slice(0, 5).map((r: any) => r.linkId));
        }
      }

      console.log('=== QUERY RESULT ===');
      console.log('Full result:', JSON.stringify(result, null, 2));

      if (!result.data || result.data.length === 0) {
        console.log('No payment link found, searching for alternatives...');
        
        // Try to find an alternative link before showing error
        const foundAlternative = await findAlternativeLink(linkId!);
        
        if (!foundAlternative) {
          console.log('No alternative links found');
          setError('Payment link not found');
          setLoading(false);
        }
        // If alternative was found, redirect will happen automatically
        return;
      }

      const link = result.data[0];
      console.log('Found payment link:', link);

      // Now get the customer card requirement data
      const requirementResult = await client.models.CustomerCardRequirement.get({
        customerId: link.customerCardRequirementId!
      });

      console.log('Requirement result:', requirementResult);

      if (!requirementResult.data) {
        console.log('No customer requirement found');
        setError('Customer requirement not found');
        setLoading(false);
        return;
      }

      const requirement = requirementResult.data;
      console.log('Found requirement:', requirement);
      
      // Check if expired
      if (new Date() > new Date(link.expiresAt)) {
        console.log('Payment link expired');
        setError('expired');
        setLoading(false);
        return;
      }

      // Check if already completed
      if (link.status === 'COMPLETED') {
        console.log('Payment already completed');
        setError('Payment has already been processed');
        setLoading(false);
        return;
      }

      // Check if processing
      if (link.status === 'MEWS_PROCESSING') {
        console.log('Payment is being processed');
        setError('Payment is currently being processed. Please wait or contact support.');
        setLoading(false);
        return;
      }

      // Handle failed status - show special retry interface
      if (link.status === 'FAILED') {
        console.log('Payment link is in failed status - allowing retry');
        // Set a flag to show retry messaging but still allow form access
        setError('Previous payment attempt failed. Please try again with new card details.');
      }

      console.log('Setting payment link data');
      
      // Combine payment link technical data with customer requirement data
      setPaymentLink({
        linkId: link.linkId,
        guestName: requirement.guestName,           
        guestEmail: requirement.guestEmail,
        locationName: requirement.locationName,
        confirmationNumber: requirement.confirmationNumber,
        arrivalDate: requirement.arrivalDate,     
        departureDate: requirement.departureDate,
        amount: requirement.amount,
        currency: requirement.currency,
        status: link.status,
        expiresAt: link.expiresAt,
        metadata: link.metadata,
        customerId: requirement.customerId, 
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching payment link:', err);
      setError('Failed to load payment details');
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (tokenData: any) => {
    console.log('=== PAYMENT SUCCESS ===');
    console.log('Token received:', tokenData);
    
    // Clear any timeout immediately
    if (secureFieldsRef.current && (secureFieldsRef.current as any).timeoutId) {
      clearTimeout((secureFieldsRef.current as any).timeoutId);
      (secureFieldsRef.current as any).timeoutId = null;
    }
  
    // Clear any existing errors
    setError(null);
    setProcessing(false);
  
    try {
      // Get the record using enhanced scan approach
      console.log('🔍 Finding record for linkId:', linkId);
      let result = await client.models.PaymentLink.list({
        filter: { linkId: { eq: linkId } }
      });
      
      // If GSI query returns empty, try scan approach as fallback
      if (!result.data || result.data.length === 0) {
        console.log('GSI query returned empty for payment update, trying scan approach...');
        
        let scanResult = await client.models.PaymentLink.list({
          limit: 1000
        });
        
        let allRecords = scanResult.data || [];
        let nextToken = scanResult.nextToken;
        
        // Paginate through all records if needed
        while (nextToken && allRecords.length < 3000) {
          const nextPage = await client.models.PaymentLink.list({
            limit: 1000,
            nextToken: nextToken
          });
          
          if (nextPage.data) {
            allRecords = allRecords.concat(nextPage.data);
          }
          nextToken = nextPage.nextToken;
        }
        
        // Filter client-side for the linkId
        const matchingLink = allRecords.find((link: any) => link.linkId === linkId);
        
        if (matchingLink) {
          result = { data: [matchingLink], nextToken: null };
          console.log('Found link via scan approach for payment update');
        } else {
          console.error('❌ No record found via scan approach');
          setError('Payment processed but record not found.');
          return;
        }
      }
  
      const record = result.data[0];
      console.log('✅ Found record:', record.id, 'status:', record.status);
      
      // Extract transaction ID from tokenData
      const transactionId = tokenData?.transactionId || null;
      console.log('💳 Transaction ID:', transactionId);
  
      // Get expiry values from form for CC expiration
      const expiryMonth = (document.getElementById('expiry-month') as HTMLSelectElement)?.value;
      const expiryYear = (document.getElementById('expiry-year') as HTMLSelectElement)?.value;
      
      // Format expiration as YYYY-MM for Mews API compatibility
      let ccExpiration = null;
      if (expiryMonth && expiryYear) {
        const fullYear = expiryYear.length === 2 ? `20${expiryYear}` : expiryYear;
        ccExpiration = `${fullYear}-${expiryMonth}`;
      }
      console.log('💳 CC Expiration:', ccExpiration);
      
      // Update with status MEWS_PROCESSING, attempts, tokenData, and expiration
      console.log('🔄 Updating status to MEWS_PROCESSING with transaction ID...');
      
      // If this was a FAILED payment, we're doing a retry - clear previous error metadata
      const isRetry = record.status === 'FAILED';
      console.log(`${isRetry ? '🔄 RETRY ATTEMPT' : '🆕 NEW ATTEMPT'} for record ${record.id}`);
      
      const updateResult = await client.models.PaymentLink.update({
        id: record.id,
        status: 'MEWS_PROCESSING',
        attempts: (record.attempts || 0) + 1,
        updatedAt: new Date().toISOString(),
        tokenData: transactionId,
        expiration: ccExpiration,
        // Clear any previous error metadata and set processing metadata
        metadata: JSON.stringify({
          processingStartedAt: new Date().toISOString(),
          pciProxyTransactionId: transactionId,
          isRetry: isRetry,
          previousStatus: record.status
        })
      });
      
      console.log('📋 Update result:', JSON.stringify(updateResult, null, 2));
      
      if (updateResult.errors && updateResult.errors.length > 0) {
        console.error('❌ Update failed:', updateResult.errors);
        setError('Failed to update payment status.');
        return;
      }
      
      console.log('✅ Status updated to MEWS_PROCESSING successfully!');
      console.log('ℹ️ Mews processing will be handled automatically by DynamoDB trigger');
      
      setSuccess(true);
      
    } catch (error) {
      console.error('❌ Exception during update:', error);
      setError('Payment processed but failed to save.');
      
      // Try to update the record to FAILED status if possible
      try {
        const result = await client.models.PaymentLink.list({
          filter: { linkId: { eq: linkId } }
        });
        
        if (result.data && result.data.length > 0) {
          const record = result.data[0];
          await client.models.PaymentLink.update({
            id: record.id,
            status: 'FAILED',
            attempts: (record.attempts || 0) + 1,
            updatedAt: new Date().toISOString(),
            metadata: {
              errorCode: 'PAYMENT_UPDATE_ERROR',
              errorMessage: error instanceof Error ? error.message : 'Failed to update payment record',
              lastFailedAt: new Date().toISOString()
            }
          });
          console.log('✅ Updated record to FAILED status');
        }
      } catch (updateError) {
        console.error('❌ Failed to update record to FAILED status:', updateError);
      }
    }
  };

  const initializeSecureFields = () => {
    if (!window.SecureFields || secureFieldsRef.current) return;

    console.log('=== CREATING NEW SECURE FIELDS INSTANCE ===');
    console.log('window.location.hostname:', window.location.hostname);
    console.log('window.location.protocol:', window.location.protocol);
    console.log('Current URL:', window.location.href);
    
    const secureFields = new window.SecureFields();
    secureFieldsRef.current = secureFields;

    // Initialize tokenize with production merchant ID
    console.log('=== INITIALIZING PCI PROXY SECURE FIELDS ===');
    console.log('Merchant ID:', '3000013748');
    console.log('Environment: PRODUCTION (no sandbox)');
    const scriptElement = document.querySelector('script[src*="pay.datatrans.com"]') as HTMLScriptElement;
    console.log('Script source confirmed:', scriptElement?.src);
    
    secureFields.initTokenize("3000013748", {
      cardNumber: "card-number-placeholder",
      cvv: "cvv-placeholder"
    });
    
    console.log('=== SECURE FIELDS INITIALIZED ===');

    // Clear placeholder styling when PCI Proxy takes over
    const cardNumberEl = document.getElementById('card-number-placeholder');
    const cvvEl = document.getElementById('cvv-placeholder');
    if (cardNumberEl) cardNumberEl.style.setProperty('--placeholder', 'none');
    if (cvvEl) cvvEl.style.setProperty('--placeholder', 'none');

    // Handle success
    secureFields.on("success", (data: any) => {
      console.log('PCI Proxy success event:', data);
      if (data.transactionId) {
        handlePaymentSuccess(data);
      } else {
        console.log('No transactionId in success data');
        setError('Payment processing failed - no transaction ID received.');
        setProcessing(false);
      }
    });

    // Handle errors
    secureFields.on("error", (error: any) => {
      console.error('=== PCI PROXY ERROR DETECTED ===');
      console.error('Error object:', error);
      console.error('Error type:', typeof error);
      console.error('Error message:', error?.message || error?.error || 'No message');
      console.error('Error code:', error?.code || 'No code');
      console.error('Current merchant ID:', '3000013748');
      console.error('Current script source:', scriptElement?.src);
      console.error('=== END PCI PROXY ERROR ===');
      setProcessing(false);
      
      // Parse different types of errors
      let errorMessage = 'Payment processing failed. Please try again.';
      let errorCode = 'PCI_PROXY_ERROR';
      
      if (error && typeof error === 'object') {
        if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        } else if (error.code) {
          switch (error.code) {
            case 'INVALID_CARD_NUMBER':
              errorMessage = 'Invalid card number. Please check and try again.';
              errorCode = 'INVALID_CARD_NUMBER';
              break;
            case 'INVALID_CVV':
              errorMessage = 'Invalid CVV. Please check and try again.';
              errorCode = 'INVALID_CVV';
              break;
            case 'EXPIRED_CARD':
              errorMessage = 'Card has expired. Please use a different card.';
              errorCode = 'EXPIRED_CARD';
              break;
            case 'INSUFFICIENT_FUNDS':
              errorMessage = 'Insufficient funds. Please use a different card.';
              errorCode = 'INSUFFICIENT_FUNDS';
              break;
            case 'DECLINED':
              errorMessage = 'Card was declined. Please use a different card.';
              errorCode = 'CARD_DECLINED';
              break;
            default:
              errorMessage = `Payment failed: ${error.code}`;
              errorCode = error.code;
          }
        }
      }
      
      setError(errorMessage);
      setErrorAttempts(prev => prev + 1);
      
      // Update database with failed attempt
      updateFailedAttempt(errorMessage, errorCode, 'PROCESSING');
    });

    // Handle validation errors (for invalid card format before submission)
    secureFields.on("validate", (data: any) => {
      console.log('=== PCI PROXY VALIDATION EVENT ===');
      console.log('Full validation data:', JSON.stringify(data, null, 2));
      console.log('Has errors:', data.hasErrors);
      
      if (data.hasErrors) {
        console.log('=== VALIDATION ERRORS DETECTED ===');
        console.log('Fields data:', JSON.stringify(data.fields, null, 2));
        
        setProcessing(false);
        
        let errorMessage = 'Please check your card details.';
        let errorCode = 'VALIDATION_ERROR';
        
        // Check specific field errors
        if (data.fields) {
          console.log('Checking field errors...');
          
          if (data.fields.cardNumber) {
            console.log('Card number field:', JSON.stringify(data.fields.cardNumber, null, 2));
            if (!data.fields.cardNumber.valid) {
              errorMessage = 'Please enter a valid card number.';
              errorCode = 'INVALID_CARD_FORMAT';
              console.log('Card number is invalid');
            }
          }
          
          if (data.fields.cvv) {
            console.log('CVV field:', JSON.stringify(data.fields.cvv, null, 2));
            if (!data.fields.cvv.valid) {
              errorMessage = 'Please enter a valid CVV.';
              errorCode = 'INVALID_CVV_FORMAT';
              console.log('CVV is invalid');
            }
          }
        }
        
        console.log('Final error message:', errorMessage);
        console.log('Final error code:', errorCode);
        
        setError(errorMessage);
        setErrorAttempts(prev => {
          const newAttempts = prev + 1;
          console.log('Updating error attempts from', prev, 'to', newAttempts);
          return newAttempts;
        });
        
        // Update database for validation errors - these are user interaction attempts
        console.log('Calling updateFailedAttempt...');
        updateFailedAttempt(errorMessage, errorCode, 'VALIDATION');
        
        // Clear any timeout since we're stopping the process
        if (secureFieldsRef.current && (secureFieldsRef.current as any).timeoutId) {
          clearTimeout((secureFieldsRef.current as any).timeoutId);
        }
        
        console.log('Returning false to prevent submission');
        return false; // Prevent submission
      }
      
      console.log('No validation errors, returning true');
      return true;
    });
  };

  const handlePaymentSubmit = () => {
    if (!secureFieldsRef.current || !paymentLink) return;

    // Clear any previous errors
    setError(null);
    setProcessing(true);

    // Get expiry values from form
    const expiryMonth = (document.getElementById('expiry-month') as HTMLSelectElement)?.value;
    const expiryYear = (document.getElementById('expiry-year') as HTMLSelectElement)?.value;

    // Validate expiry fields
    if (!expiryMonth || !expiryYear) {
      setError('Please select expiration month and year.');
      setProcessing(false);
      return;
    }

    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      // Only show timeout error if we're not already in success state
      if (!success) {
        const timeoutError = 'Payment processing timed out. Please try again.';
        setError(timeoutError);
        setProcessing(false);
        // Update database for timeout - this is a real failed attempt
        updateFailedAttempt(timeoutError, 'PAYMENT_TIMEOUT', 'TIMEOUT');
      }
    }, 30000); // 30 second timeout

    // Store timeout ID so we can clear it on success/error
    (secureFieldsRef.current as any).timeoutId = timeoutId;

    try {
      // Submit to PCI Proxy for tokenization
      secureFieldsRef.current.submit({
        expm: expiryMonth,
        expy: expiryYear,
        usage: "SIMPLE",
        email: paymentLink.guestEmail,
        phoneNumber: "123456789" // Optional
      });
    } catch (error) {
      console.error('Error submitting to PCI Proxy:', error);
      clearTimeout(timeoutId);
      const submitError = 'Failed to submit payment. Please try again.';
      setError(submitError);
      setProcessing(false);
      // Update database for submission errors - this is a real failed attempt
      updateFailedAttempt(submitError, 'SUBMISSION_ERROR', 'SUBMISSION');
    }
  };

  const resetSecureFields = () => {
    // Don't reset secure fields if payment was successful
    if (success) {
      console.log('Payment successful, not resetting secure fields');
      return;
    }

    // Destroy the current instance if it exists
    if (secureFieldsRef.current) {
      try {
        if (typeof secureFieldsRef.current.destroy === 'function') {
          secureFieldsRef.current.destroy();
        }
      } catch (error) {
        console.log('Error destroying secure fields:', error);
      }
      secureFieldsRef.current = null;
    }
    
    // Clear the DOM elements
    try {
      const cardNumberElement = document.getElementById('card-number-placeholder');
      const cvvElement = document.getElementById('cvv-placeholder');
      
      if (cardNumberElement) {
        cardNumberElement.innerHTML = '';
      }
      if (cvvElement) {
        cvvElement.innerHTML = '';
      }
    } catch (error) {
      console.log('Error clearing DOM elements:', error);
    }
    
    // Reinitialize after a short delay to ensure DOM is ready
    setTimeout(() => {
      try {
        if (window.SecureFields && paymentLink && !success) {
          initializeSecureFields();
        }
      } catch (error) {
        console.log('Error reinitializing secure fields:', error);
      }
    }, 100);
  };

  const handleErrorDismiss = () => {
    setError(null);
    setProcessing(false);
    resetSecureFields();
  };

  const updateFailedAttempt = async (errorMessage: string, errorCode?: string, errorType?: string) => {
    try {
      console.log('=== UPDATING FAILED ATTEMPT ===');
      console.log('Link ID:', linkId);
      console.log('Error message:', errorMessage);
      console.log('Error code:', errorCode);
      console.log('Error type:', errorType);
      
      const result = await client.models.PaymentLink.list({
        filter: { linkId: { eq: linkId } }
      });

      if (result.data && result.data.length > 0) {
        const record = result.data[0];
        const newAttemptCount = (record.attempts || 0) + 1;
        
        console.log('Current attempts:', record.attempts);
        console.log('New attempt count:', newAttemptCount);
        console.log('Current status:', record.status);
        
        // Determine error code if not provided
        let finalErrorCode = errorCode || 'PCI_PROXY_ERROR';
        if (!errorCode) {
          if (errorMessage.includes('INVALID_CARD_NUMBER') || errorMessage.includes('card number')) {
            finalErrorCode = 'INVALID_CARD_NUMBER';
          } else if (errorMessage.includes('INVALID_CVV') || errorMessage.includes('CVV')) {
            finalErrorCode = 'INVALID_CVV';
          } else if (errorMessage.includes('EXPIRED_CARD') || errorMessage.includes('expired')) {
            finalErrorCode = 'EXPIRED_CARD';
          } else if (errorMessage.includes('INSUFFICIENT_FUNDS') || errorMessage.includes('funds')) {
            finalErrorCode = 'INSUFFICIENT_FUNDS';
          } else if (errorMessage.includes('DECLINED') || errorMessage.includes('declined')) {
            finalErrorCode = 'CARD_DECLINED';
          } else if (errorMessage.includes('timed out')) {
            finalErrorCode = 'PAYMENT_TIMEOUT';
          } else if (errorMessage.includes('Failed to submit')) {
            finalErrorCode = 'SUBMISSION_ERROR';
          }
        }
        
        console.log('Final error code:', finalErrorCode);
        
        const newStatus = newAttemptCount >= 5 ? 'FAILED' : 'PENDING';
        console.log('New status will be:', newStatus);
        
        // Ensure all metadata values are strings and not undefined
        const metadata = {
          code: String(finalErrorCode),
          message: String(errorMessage).substring(0, 200), // Limit length
          type: String(errorType || 'UNKNOWN'),
          timestamp: new Date().toISOString(),
          count: newAttemptCount, // Keep as number
          status: String(record.status || 'UNKNOWN')
        };
        
        const updateData = {
          id: record.id,
          status: newStatus,
          attempts: newAttemptCount,
          updatedAt: new Date().toISOString(),
          metadata: JSON.stringify(metadata)
        };
        
        console.log('=== UPDATE DATA ===');
        console.log(JSON.stringify(updateData, null, 2));
        
        const updateResult = await client.models.PaymentLink.update(updateData);
        
        console.log('=== UPDATE RESULT ===');
        console.log(JSON.stringify(updateResult, null, 2));
        
        if (updateResult.errors && updateResult.errors.length > 0) {
          console.error('❌ Failed to update failed attempt:', updateResult.errors);
          console.error('Error details:', JSON.stringify(updateResult.errors, null, 2));
          
          // Try a very simple update with minimal metadata
          console.log('Trying very simple update...');
          try {
            const verySimpleUpdateResult = await client.models.PaymentLink.update({
              id: record.id,
              status: newStatus,
              attempts: newAttemptCount,
              updatedAt: new Date().toISOString(),
              metadata: `${finalErrorCode}: ${errorMessage} at ${new Date().toISOString()}`
            });
            console.log('Very simple update result:', verySimpleUpdateResult);
            
            if (verySimpleUpdateResult.errors && verySimpleUpdateResult.errors.length > 0) {
              console.error('Even simple update failed:', verySimpleUpdateResult.errors);
              
              // Try updating without metadata at all
              console.log('Trying update without metadata...');
              const noMetadataResult = await client.models.PaymentLink.update({
                id: record.id,
                status: newStatus,
                attempts: newAttemptCount,
                updatedAt: new Date().toISOString()
              });
              console.log('No metadata update result:', noMetadataResult);
            } else {
              console.log('✅ Simple update succeeded');
            }
          } catch (simpleError) {
            console.error('Exception in simple update:', simpleError);
          }
        } else {
          console.log('✅ Successfully updated failed attempt');
          console.log('✅ New record status:', newStatus);
          console.log('✅ New attempt count:', newAttemptCount);
        }
      } else {
        console.error('❌ No payment link record found to update');
      }
    } catch (error) {
      console.error('❌ Exception in updateFailedAttempt:', error);
      if (error instanceof Error) {
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
    }
  };

  const handleExpiredLink = async () => {
    try {
      // Don't clear the error state - we need to keep it as 'expired'
      setProcessing(true);
      
      console.log('🔄 Requesting replacement payment link for expired linkId:', linkId);
      
      // Call AWS Lambda function via API Gateway
      const response = await fetch('https://emvemkx2r6.execute-api.us-west-2.amazonaws.com/prod/send-replacement-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          expiredLinkId: linkId
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Lambda response:', result);
        console.log('result.success:', result.success);
        console.log('result.emailSent:', result.emailSent);
        
        if (result.success && result.emailSent) {
          console.log('✅ Email sent successfully, updating UI');
          setEmailSent(true);
          setProcessing(false);
        } else {
          console.log('❌ Email not sent - result:', result);
          setError(result.message || 'Unable to send payment link. Please contact our guest care team.');
          setProcessing(false);
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.log('❌ API error response:', errorData);
        setError(`Unable to send payment link: ${errorData.message || 'Please contact support'}`);
        setProcessing(false);
      }
    } catch (error) {
      console.error('❌ Error sending replacement payment link:', error);
      setError('Unable to send payment link. Please contact our guest care team directly.');
      setProcessing(false);
    }
  };

  // Logo component
  const Logo = () => (
    <div className="logo">
      <img 
        src="http://cdn.mcauto-images-production.sendgrid.net/a1fe588379782815/8aaa4a53-ccba-431e-b648-fde0e2fae740/4501x834.png" 
        alt="Mint House"
        style={{ 
          height: '65px', 
          width: 'auto',
          objectFit: 'contain',
          mixBlendMode: 'multiply'
        }}
      />
    </div>
  );

  // Now all the conditional returns come AFTER all hooks
  if (loading) {
    return (
      <div className="payment-page">
        <div className="container">
          <div className="loading-card">
            <div className="header">
              <Logo />
            </div>
            <div className="content">
              <div className="spinner"></div>
              <p>Loading payment details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error === 'expired') {
    console.log('Error is expired, emailSent state:', emailSent);
    if (emailSent) {
      return (
        <div className="payment-page">
          <div className="container">
            <div className="success-card">
              <div className="header">
                <Logo />
              </div>
              <div className="content">
                <h2>Email Sent!</h2>
                <p style={{ marginBottom: '24px' }}>We've sent a new payment link to your email address. Please check your inbox and spam folder.</p>
                <div style={{ 
                  background: '#f8fffe', 
                  border: '1px solid #e8f5f0', 
                  borderRadius: '12px', 
                  padding: '16px', 
                  marginBottom: '24px',
                  fontSize: '14px',
                  color: '#666'
                }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#00180E' }}>⏱️ Please allow up to 5 minutes for delivery</p>
                  <p style={{ margin: '0' }}>If you don't receive the email, please check your spam folder or contact our guest care team.</p>
                </div>
                <div className="contact-info">
                  <p>Need immediate assistance?</p>
                  <div className="contact-links">
                    <a href="tel:+18559729090">📞 1-855-972-9090</a>
                    <a href="sms:+16468467374">📱 1-646-846-7374</a>
                    <a href="mailto:info@minthouse.com">✉️ info@minthouse.com</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="payment-page">
        <div className="container">
          <div className="error-card">
            <div className="header">
              <Logo />
            </div>
            <div className="content">
              <div className="error-icon">⏰</div>
              <h2>Payment Link Expired</h2>
              <p style={{ marginBottom: '32px' }}>This payment link has expired for your security. We can send you the current active payment link via email.</p>
              <button 
                onClick={handleExpiredLink}
                className="primary-button"
                style={{ marginBottom: '24px' }}
                disabled={processing}
              >
                {processing ? 'Sending...' : 'Email Current Payment Link'}
              </button>
              <div className="contact-info">
                <p>Need immediate assistance?</p>
                <div className="contact-links">
                  <a href="tel:+18559729090">📞 1-855-972-9090</a>
                  <a href="sms:+16468467374">📱 1-646-846-7374</a>
                  <a href="mailto:info@minthouse.com">✉️ info@minthouse.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle payment link not found - this should be a hard error that prevents form rendering
  if (error === 'Payment link not found' || error === 'Customer requirement not found') {
    if (emailSent) {
      return (
        <div className="payment-page">
          <div className="container">
            <div className="success-card">
              <div className="header">
                <Logo />
              </div>
              <div className="content">
                <h2>Request Sent!</h2>
                <p style={{ marginBottom: '24px' }}>We've sent your request to our team. If you have a reservation with us, we'll send you a new payment link via email shortly.</p>
                <div style={{ 
                  background: '#f8fffe', 
                  border: '1px solid #e8f5f0', 
                  borderRadius: '12px', 
                  padding: '16px', 
                  marginBottom: '24px',
                  fontSize: '14px',
                  color: '#666'
                }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#00180E' }}>⏱️ Please allow up to 15 minutes for processing</p>
                  <p style={{ margin: '0' }}>If you don't receive an email, please contact our guest care team directly.</p>
                </div>
                <div className="contact-info">
                  <p>Need immediate assistance?</p>
                  <div className="contact-links">
                    <a href="tel:+18559729090">📞 1-855-972-9090</a>
                    <a href="sms:+16468467374">📱 1-646-846-7374</a>
                    <a href="mailto:info@minthouse.com">✉️ info@minthouse.com</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="payment-page">
        <div className="container">
          <div className="error-card">
            <div className="header">
              <Logo />
            </div>
            <div className="content">
              <div className="error-icon">🔍</div>
              <h2>Payment Link Not Found</h2>
              <p style={{ marginBottom: '32px' }}>
                This payment link is not valid or may have been removed. This could happen if you're using an old or incorrect link.
              </p>
              
              <div style={{ 
                background: '#f8fffe', 
                border: '1px solid #e8f5f0', 
                borderRadius: '12px', 
                padding: '16px', 
                marginBottom: '24px',
                fontSize: '14px',
                color: '#666'
              }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#00180E' }}>💡 What you can do:</p>
                <p style={{ margin: '0 0 8px 0' }}>• Request a new payment link using the button below</p>
                <p style={{ margin: '0 0 8px 0' }}>• Check your email for the latest payment link</p>
                <p style={{ margin: '0' }}>• Contact our guest care team for immediate assistance</p>
              </div>

              <button 
                onClick={handleExpiredLink}
                className="primary-button"
                style={{ marginBottom: '24px' }}
              >
                Request New Payment Link
              </button>

              <div className="contact-info">
                <p>Contact our guest care team:</p>
                <div className="contact-links">
                  <a href="tel:+18559729090">📞 1-855-972-9090</a>
                  <a href="sms:+16468467374">📱 1-646-846-7374</a>
                  <a href="mailto:info@minthouse.com">✉️ info@minthouse.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && error !== 'expired' && errorAttempts >= 3) {
    return (
      <div className="payment-page">
        <div className="container">
          <div className="error-card">
            <div className="header">
              <Logo />
            </div>
            <div className="content">
              <div className="error-icon">⚠️</div>
              <h2>Payment Issue</h2>
              <p>{error}</p>
              
              <div style={{ marginTop: '20px' }}>
                <button 
                  onClick={() => {
                    setError(null);
                    setProcessing(false);
                    resetSecureFields();
                  }} 
                  className="primary-button"
                  style={{ marginRight: '10px' }}
                >
                  Try Again
                </button>
                <button 
                  onClick={() => navigate('/')} 
                  className="secondary-button"
                >
                  Return to Home
                </button>
              </div>

              <div className="contact-info" style={{ marginTop: '30px' }}>
                <p style={{ fontSize: '14px', color: '#666' }}>
                  Still having trouble? Our guest care team is here to help:
                </p>
                <div className="contact-links">
                  <a href="tel:+18559729090" style={{ margin: '0 10px' }}>📞 1-855-972-9090</a>
                  <a href="sms:+16468467374" style={{ margin: '0 10px' }}>📱 1-646-846-7374</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="payment-page">
        <div className="container">
          <div className="success-card">
            <div className="header">
              <Logo />
            </div>
            <div className="success-state">
              <div className="success-icon">✓</div>
              <h2 className="success-title">Payment Method Added</h2>
              <p className="success-message">Your card has been securely saved to your guest profile. We look forward to welcoming you!</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <div className="container">
        <div className="payment-card">
          <div className="header">
            <Logo />
          </div>

          <div className="content">
            <div className="welcome-section">
              <h1 className="title">Add Payment Method</h1>
              <p className="description">
                Securely save your payment method for a seamless check-in experience at Mint House.
              </p>
              <div className="help-section">
                <h2>🛎️ Need Help?</h2>
                <p>We're here 24/7.</p>
                <div className="contact-methods">
                  <div className="contact-method">
                    <span className="contact-label">Email us at</span>
                    <a href="mailto:info@minthouse.com">info@minthouse.com</a>
                  </div>
                  <div className="contact-method">
                    <span className="contact-label">Text us at</span>
                    <a href="sms:+16468467374">1-646-846-7374</a>
                  </div>
                  <div className="contact-method">
                    <span className="contact-label">Call us at</span>
                    <a href="tel:+18559729090">1-855-972-9090</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="guest-info">
              <div className="guest-header">
                <div className="guest-details">
                  <h4>{paymentLink?.guestName || 'Guest'}</h4>
                  <div className="location-name">
                    {paymentLink?.locationName || 'Mint House Property'}
                  </div>
                </div>
              </div>
              
              <div className="reservation-details">
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{paymentLink?.guestEmail}</span>
                </div>
                {paymentLink?.confirmationNumber && (
                  <div className="detail-item">
                    <span className="detail-label">Reservation</span>
                    <span className="detail-value">{paymentLink?.confirmationNumber}</span>
                  </div>
                )}
                {paymentLink?.arrivalDate && (
                  <div className="detail-item">
                    <span className="detail-label">Check-in</span>
                    <span className="detail-value">
                      {new Date(paymentLink.arrivalDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}
                {paymentLink?.departureDate && (
                  <div className="detail-item">
                    <span className="detail-label">Check-out</span>
                    <span className="detail-value">
                      {new Date(paymentLink.departureDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Error Banner */}
            {error && errorAttempts < 3 && (
              <div className={`error-banner ${paymentLink?.status === 'FAILED' ? 'retry-banner' : ''}`}>
                <div className="error-icon">{paymentLink?.status === 'FAILED' ? '🔄' : '⚠️'}</div>
                <div className="error-content">
                  <div className="error-title">
                    {paymentLink?.status === 'FAILED' ? 'Retry Payment' : 'Please try again'}
                  </div>
                  <div className="error-message">{error}</div>
                  {(errorAttempts >= 2 || paymentLink?.status === 'FAILED') && (
                    <div style={{ marginTop: '8px', fontSize: '14px' }}>
                      Having trouble? Contact our team: 
                      <a href="tel:+18559729090" style={{ color: '#856404', marginLeft: '5px' }}>📞 1-855-972-9090</a>
                    </div>
                  )}
                </div>
                {paymentLink?.status !== 'FAILED' && (
                  <button 
                    onClick={handleErrorDismiss}
                    className="error-dismiss"
                  >
                    ×
                  </button>
                )}
              </div>
            )}

            <div className="form-section">
              <div className="section-title">
                💳 Payment Information
              </div>

              <form className="payment-form" onSubmit={(e) => e.preventDefault()}>
                <div className="form-group">
                  <label className="form-label" htmlFor="card-number-placeholder">Card Number</label>
                  <div id="card-number-placeholder" className="secure-field"></div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="expiry-month">Expiration Month</label>
                    <select id="expiry-month" className="form-select" required>
                      <option value="">MM</option>
                      {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                        <option key={month} value={month.toString().padStart(2, '0')}>
                          {month.toString().padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="expiry-year">Expiration Year</label>
                    <select id="expiry-year" className="form-select" required>
                      <option value="">YY</option>
                      {Array.from({length: 10}, (_, i) => new Date().getFullYear() + i).map(year => (
                        <option key={year} value={year.toString().slice(-2)}>
                          {year.toString().slice(-2)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="cvv-placeholder">CVV</label>
                    <div id="cvv-placeholder" className="secure-field"></div>
                  </div>
                </div>

                <button 
                  type="button" 
                  onClick={handlePaymentSubmit}
                  disabled={processing || !scriptLoaded}
                  className="submit-button"
                >
                  {processing ? (
                    <>
                      <div className="loading-spinner"></div>
                      <span>Securing card...</span>
                    </>
                  ) : (
                    <span>{paymentLink?.status === 'FAILED' ? 'Retry Payment' : 'Save Payment Method'}</span>
                  )}
                </button>
              </form>
            </div>

            <div className="security-footer">
              <div className="security-badge">
                <span>🔒</span>
                <span className="security-text">Bank-level security • PCI DSS compliant</span>
              </div>
              
              <div className="trust-indicators">
                <div className="trust-item">
                  <span>🛡️</span>
                  <span>SSL Encrypted</span>
                </div>
                <div className="trust-item">
                  <span>🏛️</span>
                  <span>GDPR Compliant</span>
                </div>
                <div className="trust-item">
                  <span>✓</span>
                  <span>Secure Storage</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;